import { useMediaQuery, Card, CardContent, Typography, Grid, Box } from '@mui/material'
import React from 'react'
import { StyledButton, SurveyCompletionState as surveyCompletionState, type SurveyCompletionState as StyleSurveyCompletionState } from './styles'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { grey } from '../../../../colors'
import ExpandableText from '../../../../components/ExpandableText'
import { studentSurveyListNavigatePath } from '../../../../helpers/buildPublicSurveyShareUrlFromApi'
import { formatDate } from '../../../../utils/utils'
import SandboxBadge from '../../../../components/SandboxBadge'

type SurveyCompletionState = StyleSurveyCompletionState

export interface SurveyListCardProps {
  title: string
  surveyId: string
  surveyState: SurveyCompletionState
  owner: string[]
  endDate: string
  surveyURL?: string
  onClick?: () => void
  index: number
  total: number
  courseId: string
  /**
   * Sandbox mode on the owning Survey Project (CR-341). Absent → treated as false.
   *
   * Back on the student card since the full-cycle spec (approved 2026-09-21). It was dropped when
   * sandbox froze the lifecycle, on the reasoning that a student could never receive the row — a
   * sandboxed term generated no distributions. It now runs its schedule, and a student who holds a
   * system-level role is emailed like any other, so the row reaches them and has to say what it is.
   */
  isSandbox?: boolean
}

const SurveyListCard: React.FC<SurveyListCardProps> = ({
  title,
  surveyId,
  surveyState,
  owner,
  endDate,
  surveyURL,
  onClick = () => { },
  index,
  total,
  courseId,
  isSandbox
}): JSX.Element => {
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const endDateStr = formatDate(endDate)
  const handleClick = (): void => {
    const path = studentSurveyListNavigatePath(surveyURL, surveyId)
    navigate(path)

    onClick()
  }

  return (
    <Card sx={{ marginBottom: '2rem', width: { xs: '100%', sm: '500px' }, maxWidth: '500px' }} aria-label={`Survey ${index + 1} of ${total} ${title}`} tabIndex={0} >
      <CardContent sx={{
        padding: { xs: theme.spacing(3), sm: theme.spacing(4), md: theme.spacing(5) },
        '&:last-child': {
          paddingBottom: { xs: theme.spacing(3), sm: theme.spacing(4), md: theme.spacing(5) }
        }
      }}>
        <Grid container>
          <Grid item xs={12} sm={12} md={8} >
            <Box mb={4}>
            <Typography variant="p4" gutterBottom tabIndex={0} sx={{ color: grey[850], fontWeight: 400, fontSize: '16px' }}>
                {title}
              </Typography>
            <Box display="flex" alignItems="center" gap={4}>
              <Typography variant="h5" gutterBottom tabIndex={0} sx={{ color: grey[250], fontSize: '20px', mt: 2 }}>
                {courseId}
              </Typography>
              {/* Matches the instructor card: the badge sits on the section line, not on its own
                  row above the course name. */}
              <SandboxBadge isSandbox={isSandbox} />
            </Box>
              <ExpandableText
                variant="p4"
                aria-label={`Instructors ${owner.join(', ')}`}
                sx={{ color: grey[850], fontWeight: 400, fontSize: '16px' }}
              >
                {owner.join(', ')}
              </ExpandableText>
            </Box>

            <Typography sx={{ color: grey[250], fontWeight: 600, fontSize: '14px' }}>Survey Ends</Typography>
            <Typography variant="caption" tabIndex={0} sx={{ fontSize: '14px' }} >
              {endDateStr}
            </Typography>

          </Grid>

          <Grid item xs={12} sm={12} md={4} textAlign={'end'}>
            <StyledButton aria-label={'Press Enter to Start survey'} sx={{ mt: isMobile ? 4 : 0 }} fullWidth={isMobile} tabIndex={0} variant="contained" onClick={handleClick} surveyState={surveyState} disableElevation disabled={surveyState === surveyCompletionState.Submitted || surveyState === surveyCompletionState.Closed}>{surveyState}</StyledButton>
          </Grid>
        </Grid>

      </CardContent>
    </Card>
  )
}

export default SurveyListCard
